import React, { useState } from 'react';
import { ChevronDown, CheckCircle2, XCircle, Clock, Shield, BookOpen, Headphones, Activity, ListChecks, MessageCircleWarning, HeartHandshake, ArrowRight, X } from 'lucide-react';
const Button = ({
  children,
  className = '',
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => {
  const handleClick = onClick ?? (() => document.getElementById('pricing-card')?.scrollIntoView({
    behavior: 'smooth',
    block: 'center'
  }));
  return <button onClick={handleClick} className={`bg-sage hover:bg-sage-dark text-white font-medium py-4 px-8 rounded-full transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md ${className}`} data-id="element-0">
      {children}
    </button>;
};
const Modal = ({
  open,
  onClose,
  title,
  children
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!open) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/40 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-label={title} data-id="element-1">
      <div className="relative bg-cream w-full max-w-2xl max-h-[85vh] rounded-3xl shadow-xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-2">
        <div className="flex items-center justify-between px-8 py-6 border-b border-sage/15" data-id="element-3">
          <h3 className="font-serif text-2xl text-charcoal" data-id="element-4">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="text-charcoal-light hover:text-charcoal transition-colors p-1 rounded-full hover:bg-sage/10" data-id="element-5">
            <X className="w-5 h-5" data-id="element-6" />
          </button>
        </div>
        <div className="px-8 py-6 overflow-y-auto text-charcoal-light leading-relaxed space-y-4" data-id="element-7">
          {children}
        </div>
      </div>
    </div>;
};
const Section = ({
  children,
  className = '',
  id
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-16 md:py-24 px-6 ${className}`} data-id="element-8">
    <div className="max-w-4xl mx-auto" data-id="element-9">{children}</div>
  </section>;
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-sage-light/30 last:border-0" data-id="element-10">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none" data-id="element-11">
        <h4 className="font-serif text-xl text-charcoal pr-8" data-id="element-12">{question}</h4>
        <ChevronDown className={`w-5 h-5 text-sage transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-13" />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-6' : 'max-h-0 opacity-0'}`} data-id="element-14">
        <p className="text-charcoal-light leading-relaxed" data-id="element-15">{answer}</p>
      </div>
    </div>;
};
export function App() {
  const [activeModal, setActiveModal] = useState<null | 'privacy' | 'terms'>(null);
  const scrollToPricing = () => {
    document.getElementById('pricing-card')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  return <div className="min-h-screen bg-cream selection:bg-sage/20" data-id="element-16">
      {/* Hero Section */}
      <Section className="pt-20 md:pt-32 pb-16 md:pb-24" data-id="element-17">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-18">
          <div className="space-y-8" data-id="element-19">
            <div className="inline-block bg-sage/10 text-sage-dark px-4 py-1.5 rounded-full text-sm font-medium tracking-wide uppercase" data-id="element-20">
              For Exhausted Parents
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-charcoal leading-tight" data-id="element-21">
              The 48-Hour Sensory Reset
            </h1>
            <p className="text-xl md:text-2xl text-charcoal-light font-serif italic leading-relaxed" data-id="element-22">
              The "No-Yell" Blueprint to End the 6:00 PM Witching Hour Chaos
              Without Raising Your Voice
            </p>
            <div className="pt-4" data-id="element-23">
              <Button onClick={scrollToPricing} className="w-full md:w-auto text-lg" data-id="element-24">
                Get Instant Access <ArrowRight className="w-5 h-5" data-id="element-25" />
              </Button>
              <p className="text-sm text-charcoal-light mt-4 text-center md:text-left" data-id="element-26">
                Instant digital download. Delivered to your inbox.
              </p>
            </div>
          </div>
          <div className="relative" data-id="element-27">
            <div className="absolute inset-0 bg-sage/10 rounded-3xl transform rotate-3 scale-105" data-id="element-28"></div>
            <img src="https://cdn.magicpatterns.com/uploads/4P7QcWDRPV1KR8QwgJqCng/The_48_H_-_MockUp-Cover.png" alt="The 48-Hour Sensory Reset on Tablet and Phone" className="relative z-10 w-full h-auto rounded-2xl shadow-xl object-cover" data-id="element-29" />
          </div>
        </div>
      </Section>

      {/* The Hook & Problem */}
      <Section className="bg-cream-dark rounded-3xl mx-4 md:mx-8 my-8" data-id="element-30">
        <div className="max-w-3xl mx-auto space-y-12" data-id="element-31">
          <div className="prose prose-lg prose-p:text-charcoal-light prose-p:leading-relaxed" data-id="element-32">
            <p className="text-xl md:text-2xl font-serif text-charcoal leading-relaxed" data-id="element-33">
              It’s 5:45 PM. The kitchen lights are pulsing behind your eyes. The
              TV is humming in the background. While you’re trying to get dinner
              on the table, your toddler is face-down on the floor, screaming
              because of the "wrong" colored plate.
            </p>
            <p data-id="element-34">
              You feel the heat rising in your chest. You’ve tried the gentle
              parenting scripts. You’ve tried the "I see you’re upset" whispers.
              But in this moment, you feel like you’re failing, drowning in a
              home that feels like a construction site of noise and chaos.
            </p>
          </div>

          <div className="border-t border-sage/20 pt-12" data-id="element-35">
            <h2 className="text-3xl font-serif text-charcoal mb-6" data-id="element-36">
              You aren't dealing with a discipline problem. You are dealing with
              a biology problem.
            </h2>
            <div className="space-y-6 text-charcoal-light leading-relaxed" data-id="element-37">
              <p data-id="element-38">
                Most parenting advice assumes your child is making a logical
                choice to misbehave. They aren’t. By 6:00 PM, your child’s
                nervous system is a full bucket. The itchy clothing tags, the
                daycare lights, and the transition home have filled it to the
                brim. The "triangular toast" isn’t the problem—it’s just the
                drop that made the bucket overflow.
              </p>
              <p data-id="element-39">
                When a child’s lid is flipped, you cannot reason with them. If
                your home environment is still "loud" and "bright," your words
                are just more noise.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Why What You Tried Didn't Work */}
      <Section data-id="element-40">
        <div className="text-center mb-12" data-id="element-41">
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-4" data-id="element-42">
            Why What You Tried Didn’t Work
          </h2>
          <p className="text-lg text-charcoal-light max-w-2xl mx-auto" data-id="element-43">
            Traditional methods fail when a child's nervous system is
            overloaded.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6" data-id="element-44">
          {[{
          title: 'Time-outs',
          desc: 'Create a cortisol feedback loop, making the child feel rejected and more frantic.'
        }, {
          title: 'Rational explanations',
          desc: 'Fail because the logical brain is literally "offline" during a meltdown.'
        }, {
          title: 'Sticker charts and bribes',
          desc: 'Add more sensory "noise" to an already overloaded brain.'
        }, {
          title: 'Gentle parenting narration',
          desc: 'Often happens while the child is in a "biological blackout," where they cannot process your words.'
        }].map((item, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-sage/10 flex gap-4" data-id="element-45">
              <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-46" />
              <div data-id="element-47">
                <h3 className="font-serif text-xl text-charcoal mb-2" data-id="element-48">
                  {item.title}
                </h3>
                <p className="text-charcoal-light leading-relaxed" data-id="element-49">
                  {item.desc}
                </p>
              </div>
            </div>)}
        </div>
      </Section>

      {/* The Unique Mechanism */}
      <Section className="bg-sage text-white rounded-3xl mx-4 md:mx-8 my-8" data-id="element-50">
        <div className="max-w-3xl mx-auto text-center space-y-8" data-id="element-51">
          <Shield className="w-16 h-16 mx-auto text-cream opacity-80" data-id="element-52" />
          <h2 className="text-3xl md:text-4xl font-serif" data-id="element-53">
            Introducing the Low-Stimulus Shield
          </h2>
          <p className="text-xl leading-relaxed opacity-90" data-id="element-54">
            This isn't about fixing the child; it’s about fixing the room.
          </p>
          <p className="text-lg leading-relaxed opacity-90" data-id="element-55">
            By strategically adjusting the light, sound, and physical input of
            your home, you manually lower the cortisol levels in your child’s
            blood (and yours). We use environmental physics to bring a "flipped
            lid" back into alignment.
          </p>
        </div>
      </Section>

      {/* What You Get */}
      <Section data-id="element-56">
        <div className="text-center mb-16" data-id="element-57">
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-4" data-id="element-58">
            Inside The 48-Hour Sensory Reset
          </h2>
          <p className="text-lg text-charcoal-light max-w-2xl mx-auto" data-id="element-59">
            Everything you need to transform your evenings from chaotic to calm.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-60">
          {[{
          icon: BookOpen,
          text: 'The 48-Hour Sensory Reset PDF Guide'
        }, {
          icon: Shield,
          text: 'The "Lower-Third" Visual Purge System'
        }, {
          icon: Headphones,
          text: 'The Auditory Anchor (Brown Noise Tactic)'
        }, {
          icon: Activity,
          text: 'The Proprioceptive Pivot (Heavy Work Frames)'
        }, {
          icon: ListChecks,
          text: 'The 48-Hour Household Sensory Audit Checklist'
        }, {
          icon: MessageCircleWarning,
          text: 'The Emergency "In-The-Heat-Of-The-Moment" De-Escalation Script'
        }, {
          icon: HeartHandshake,
          text: 'The "Parental Anchor" 60-second recovery protocol'
        }].map((item, i) => <div key={i} className="bg-cream-dark p-6 rounded-2xl flex flex-col items-center text-center gap-4 hover:bg-sage/5 transition-colors" data-id="element-61">
              <div className="bg-white p-3 rounded-full shadow-sm text-sage" data-id="element-62">
                <item.icon className="w-6 h-6" data-id="element-63" />
              </div>
              <p className="font-medium text-charcoal" data-id="element-64">{item.text}</p>
            </div>)}
        </div>
        <div className="mt-16 text-center" data-id="element-65">
          <Button onClick={scrollToPricing} className="mx-auto text-lg" data-id="element-66">
            Get The Complete Bundle for $6.36
          </Button>
        </div>
      </Section>

      {/* How It Works */}
      <Section className="bg-white border-y border-sage/10" data-id="element-67">
        <h2 className="text-3xl md:text-4xl font-serif text-charcoal text-center mb-16" data-id="element-68">
          How It Works
        </h2>
        <div className="grid md:grid-cols-3 gap-12 relative" data-id="element-69">
          <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-sage/20 -z-10" data-id="element-70"></div>
          {[{
          step: '01',
          title: 'Visual Purge',
          desc: 'Perform the 10-minute "Visual Purge" to lower the household "refresh rate."'
        }, {
          step: '02',
          title: 'Auditory Anchor',
          desc: 'Implement the "Auditory Anchor" to neutralize environmental triggers.'
        }, {
          step: '03',
          title: 'Deep Pressure',
          desc: 'Use "Deep Pressure" and "Heavy Work" to ground your child’s nervous system before the explosion.'
        }].map((item, i) => <div key={i} className="flex flex-col items-center text-center bg-white" data-id="element-71">
              <div className="w-24 h-24 rounded-full bg-cream-dark border-4 border-white shadow-sm flex items-center justify-center mb-6" data-id="element-72">
                <span className="font-serif text-3xl text-sage" data-id="element-73">
                  {item.step}
                </span>
              </div>
              <h3 className="font-serif text-2xl text-charcoal mb-4" data-id="element-74">
                {item.title}
              </h3>
              <p className="text-charcoal-light leading-relaxed" data-id="element-75">{item.desc}</p>
            </div>)}
        </div>
      </Section>

      {/* Who This Is For / Not For */}
      <Section data-id="element-76">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-16" data-id="element-77">
          <div className="bg-sage/5 p-8 md:p-12 rounded-3xl" data-id="element-78">
            <h3 className="text-2xl font-serif text-charcoal mb-8 flex items-center gap-3" data-id="element-79">
              <CheckCircle2 className="w-8 h-8 text-sage" data-id="element-80" />
              Who This Is For
            </h3>
            <ul className="space-y-6" data-id="element-81">
              {['Moms of toddlers (2-5) who feel like they are "walking on eggshells" at home.', 'Parents who feel guilty about wanting to "run away" during the evening chaos.', 'Families with "spirited," high-needs, or sensory-sensitive children.', 'Mothers who are exhausted by "gentle parenting" and need a biological shortcut to peace.'].map((text, i) => <li key={i} className="flex gap-4 text-charcoal-light leading-relaxed" data-id="element-82">
                  <div className="w-1.5 h-1.5 rounded-full bg-sage mt-2.5 flex-shrink-0" data-id="element-83" />
                  <span data-id="element-84">{text}</span>
                </li>)}
            </ul>
          </div>

          <div className="bg-white border border-sage/10 p-8 md:p-12 rounded-3xl" data-id="element-85">
            <h3 className="text-2xl font-serif text-charcoal mb-8 flex items-center gap-3" data-id="element-86">
              <XCircle className="w-8 h-8 text-charcoal-light/50" data-id="element-87" />
              Who This Is Not For
            </h3>
            <ul className="space-y-6" data-id="element-88">
              {['Parents looking for traditional "obedience-based" discipline techniques.', 'Those unwilling to turn off the TV or dim the lights for a few hours.', 'Anyone looking for a "magic pill" that requires zero environmental change.'].map((text, i) => <li key={i} className="flex gap-4 text-charcoal-light leading-relaxed" data-id="element-89">
                  <div className="w-1.5 h-1.5 rounded-full bg-charcoal-light/30 mt-2.5 flex-shrink-0" data-id="element-90" />
                  <span data-id="element-91">{text}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </Section>

      {/* Proof */}
      <Section className="bg-cream-dark text-center" data-id="element-92">
        <div className="max-w-3xl mx-auto" data-id="element-93">
          <Clock className="w-12 h-12 mx-auto text-sage mb-6" data-id="element-94" />
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-95">
            70% reduction in meltdown intensity
          </h2>
          <p className="text-xl text-charcoal-light leading-relaxed italic" data-id="element-96">
            "Within 48 hours of implementing the Low-Stimulus Shield, parents
            typically report a dramatic shift. This isn't magic—it's what
            happens when a nervous system finally feels safe enough to stop
            fighting."
          </p>
        </div>
      </Section>

      {/* FAQ */}
      <Section data-id="element-97">
        <div className="max-w-3xl mx-auto" data-id="element-98">
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal text-center mb-12" data-id="element-99">
            Frequently Asked Questions
          </h2>
          <div className="bg-white rounded-3xl p-6 md:p-10 shadow-sm border border-sage/10" data-id="element-100">
            <FAQItem question="Do I need to buy expensive equipment?" answer="No. You likely have everything you need (blankets, lamps, specific toys) already in your home." data-id="element-101" />
            <FAQItem question="Will this work for my 'strong-willed' child?" answer="Yes. Strong-willed children usually have the most sensitive nervous systems. They aren't fighting you; they are fighting their environment." data-id="element-102" />
            <FAQItem question="How long does the 'Reset' take to set up?" answer="You can implement the first phase in less than 15 minutes." data-id="element-103" />
          </div>
        </div>
      </Section>

      {/* Offer / CTA */}
      <Section id="offer" className="bg-sage text-white rounded-t-[3rem] mt-8 pb-32" data-id="element-104">
        <div className="max-w-3xl mx-auto text-center space-y-8" data-id="element-105">
          <h2 className="text-4xl md:text-5xl font-serif" data-id="element-106">
            Stop managing behavior and start managing biology.
          </h2>
          <p className="text-xl md:text-2xl opacity-90 font-serif italic" data-id="element-107">
            Reclaim your evenings and your relationship with your child.
          </p>

          <div id="pricing-card" className="bg-white text-charcoal p-8 md:p-12 rounded-3xl mt-12 shadow-xl max-w-xl mx-auto scroll-mt-8" data-id="element-108">
            <h3 className="text-2xl font-serif mb-2" data-id="element-109">
              The 48-Hour Sensory Reset Bundle
            </h3>
            <div className="text-5xl font-serif text-sage mb-8" data-id="element-110">$6.36</div>
            <Button onClick={() => window.open('https://peacefulparentingkits.com/l/uduqxu?wanted=true', '_blank', 'noopener,noreferrer')} className="w-full text-lg py-5" data-id="element-111">
              Get Instant Access Now
            </Button>
            <p className="text-sm text-charcoal-light mt-4" data-id="element-112">
              Your digital download will be delivered immediately to your inbox.
            </p>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-charcoal text-cream/80 px-6 py-10" data-id="element-113">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6" data-id="element-114">
          <p className="text-sm text-cream/60" data-id="element-115">
            © {new Date().getFullYear()} The 48-Hour Sensory Reset. All rights
            reserved.
          </p>
          <nav className="flex items-center gap-8" data-id="element-116">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-cream/80 hover:text-cream transition-colors" data-id="element-117">
              Privacy Policy
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-cream/80 hover:text-cream transition-colors" data-id="element-118">
              Terms &amp; Conditions
            </button>
          </nav>
        </div>
      </footer>

      <Modal open={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Privacy Policy" data-id="element-119">
        <p data-id="element-120">
          Your privacy matters to us. This policy explains how we handle the
          information you share when you purchase or access The 48-Hour Sensory
          Reset.
        </p>
        <p data-id="element-121">
          <strong className="text-charcoal" data-id="element-122">Information we collect.</strong>{' '}
          When you complete a purchase, we collect the email address you provide
          so we can deliver your digital download and send order-related
          updates.
        </p>
        <p data-id="element-123">
          <strong className="text-charcoal" data-id="element-124">How we use it.</strong> We use your
          email solely to deliver your product, provide support, and share
          occasional updates about the guide. We never sell or rent your
          personal information to third parties.
        </p>
        <p data-id="element-125">
          <strong className="text-charcoal" data-id="element-126">Your choices.</strong> You may opt
          out of non-essential emails at any time using the unsubscribe link
          included in every message. You can request deletion of your data
          whenever you wish.
        </p>
        <p data-id="element-127">
          <strong className="text-charcoal" data-id="element-128">Data security.</strong> Payments are
          processed through secure, industry-standard providers. We retain only
          the minimum information needed to fulfill your order.
        </p>
      </Modal>

      <Modal open={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Terms & Conditions" data-id="element-129">
        <p data-id="element-130">
          By purchasing and accessing The 48-Hour Sensory Reset, you agree to
          the following terms.
        </p>
        <p data-id="element-131">
          <strong className="text-charcoal" data-id="element-132">Digital product.</strong> This is a
          digital download delivered to your inbox immediately after purchase.
          No physical product will be shipped.
        </p>
        <p data-id="element-133">
          <strong className="text-charcoal" data-id="element-134">License.</strong> Your purchase
          grants you a personal, non-transferable license to use the materials
          for your own family. You may not redistribute, resell, or share the
          contents.
        </p>
        <p data-id="element-135">
          <strong className="text-charcoal" data-id="element-136">Educational use.</strong> The
          strategies provided are for educational and informational purposes
          only and are not a substitute for professional medical, psychological,
          or developmental advice. Always consult a qualified professional
          regarding your child's needs.
        </p>
        <p data-id="element-137">
          <strong className="text-charcoal" data-id="element-138">Refunds.</strong> Because this is an
          instantly delivered digital product, all sales are considered final
          unless otherwise required by applicable law.
        </p>
      </Modal>
    </div>;
}